import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll'
import Icon from '../../Icons'
import Loader from '../../Loader/Loader'
import ImproveWithAI from '../../ImproveWithAI/ImproveWithAI'
import { getSMScounter } from '../../../firebase/privateChat/firebaseActions'
import CustomReplyWindow from '../ChatWidget/CustomReplyWindow/CustomReplyWindow'
import ImagesUpload from '../../ImagesUpload/ImagesUpload'
import {
  conversationStatusMap,
  getChatTypeFromConversation,
  isMaintenanceRequestChatType,
} from '../helpers'
import { getFormatedDate } from '../../../helpers/helpers'
import useChatMessages from '../useChatMessages'

import ToggleWithText from './../../ToggleWithText/ToggleWithText'
import Message from './Message'

const MessagesContainer = ({
  currentUserData,
  selectedChat,
  isLoading,
  cancelLoading,
  managementUsers,
  currentBuildingId,
  currentBuildingTimezone,
  closedConversationsData,
  textingOptOutInfo,
  isAiWriterAvailable,
}) => {
  const { t } = useTranslation(['chat'])
  const currentBuilding = useSelector(selectActiveBuilding)

  const [providedText, setProvidedText] = useState('')
  const [sendMessageAsSMS, setSendMessageAsSMS] = useState(false)
  const [customRepliesWindowVisible, setCustomRepliesWindowVisible] = useState(false)
  const scrollDiv = useRef()
  const textInputRef = useRef(null)

  const { data: chatInboxes } = useGetInboxesWithCustom({
    buildingId: currentBuilding?.id,
    chatCustom: currentBuilding?.chatCustom,
  })

  const chatType = getChatTypeFromConversation(selectedChat)
  const isChatMTRQ = isMaintenanceRequestChatType(selectedChat)
  const selectedChatInbox = chatInboxes
    ? chatInboxes.find((inbox) => inbox.type === chatType)
    : null
  const activeSMS =
    selectedChatInbox &&
    selectedChatInbox.enableSMS &&
    selectedChatInbox.phone !== 0 &&
    !isChatMTRQ &&
    !textingOptOutInfo &&
    !isLoading

  const customReplies = selectedChatInbox ? selectedChatInbox.customReplies : []

  const isClosed =
    selectedChat?.status === conversationStatusMap.CLOSED ||
    selectedChat.status === conversationStatusMap.ARCHIVED

  const scrollToBottom = () => {
    if (!scrollDiv.current) return
    // NOTE: We scroll to "bottom" by setting `scrollTop = 0` due to `flex-direction: column-reverse` reverses `scrollTop` value
    scrollDiv.current.scrollTop = 0
  }

  const {
    isLoadedAll,
    orderedMessages,
    smsMessagesLeft,
    isSendByUs,
    fetchMoreMessages,
    onSend,
    onReSendSMS,
    onSendWithAttachements,
    setSmsMessagesLeft,
  } = useChatMessages({
    selectedChat,
    availableManagementUsers: managementUsers,
    currentUser: currentUserData,
    sendMessageAsSMS,
    selectedBuildingId: currentBuildingId,
    cancelLoadingMessages: cancelLoading,
    scrollToBottom,
  })

  const lastResidentMessage = orderedMessages.find((message) => !isSendByUs(message))

  const onSendTextMessage = () => {
    if (providedText.trim()) {
      const newProvidedText = providedText
      setProvidedText('')
      onSend(newProvidedText)
    }
  }

  const onEnterPress = (e) => {
    if (e.shiftKey && e.key === 'Enter' && providedText.trim()) {
      return null
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (providedText.trim()) {
        onSendTextMessage()
      } else {
        setProvidedText('')
      }
    }
  }

  const pickCustomReply = async (replyData) => {
    setCustomRepliesWindowVisible(false)
    setProvidedText(replyData.message)
    textInputRef.current.focus()
  }

  const onInsertAIResult = (result) => {
    setProvidedText(result)
    textInputRef.current.focus()
  }

  useEffect(() => {
    getSMScounter(currentBuildingId).then((count) => setSmsMessagesLeft(count))
    if (isChatMTRQ) {
      setSendMessageAsSMS(false)
    }
  }, [selectedChat])

  return (
    <div className="body-container">
      <div className="messages-container">
        <div className="messages">
          {isLoading ? (
            <Loader />
          ) : (
            <InfiniteScroll
              reference={scrollDiv}
              data={orderedMessages.map((mess) => ({ ...mess, id: mess._id }))}
              renderItem={(message) => (
                <Message
                  key={message._id}
                  message={message}
                  sendByUs={isSendByUs(message)}
                  reSendSMS={onReSendSMS}
                />
              )}
              renderLastItem={() => {
                const closedConversationData = closedConversationsData.find(
                  (conv) => conv.name === selectedChat.channelName,
                )
                if (closedConversationData) {
                  return (
                    <div className="message-closed-conversation">
                      {t('chat:messagesContainer.closedOn', {
                        date: getFormatedDate(closedConversationData.date, currentBuildingTimezone),
                      })}
                    </div>
                  )
                }
                return null
              }}
              onEndReached={() => fetchMoreMessages()}
              style={{
                padding: '5px 20px 0 10px',
                display: 'flex',
                flexDirection: 'column-reverse',
              }}
              isLoadedAll={isLoadedAll}
              inverse={true}
            />
          )}
        </div>
        {!isClosed && (
          <div className={`message-editor ${isLoading ? 'loading' : ''}`}>
            <React.Fragment>
              <div className="message-editor__header">
                <div className="left-side">
                  {activeSMS ? (
                    <React.Fragment>
                      <div>{t('chat:messagesContainer.sendingMessagesAs')}</div>
                      <ToggleWithText
                        text={t('chat:toggle.chat')}
                        isChecked={!sendMessageAsSMS}
                        isDisabled={false}
                        onChange={() => setSendMessageAsSMS(!sendMessageAsSMS)}
                      />
                      <ToggleWithText
                        text={t('chat:toggle.sms')}
                        color="#82D7B3"
                        isChecked={sendMessageAsSMS}
                        isDisabled={false}
                        onChange={() => setSendMessageAsSMS(!sendMessageAsSMS)}
                      />
                    </React.Fragment>
                  ) : (
                    <div>{t('chat:messagesContainer.sendMessage')}</div>
                  )}
                </div>
                <div className="right-side">
                  {customReplies.length ? (
                    <div
                      className="custom-reply-button"
                      onClick={() => setCustomRepliesWindowVisible(true)}
                    >
                      <Icon icon="message" viewBox="5 3 23 24" />
                      <div>{t('chat:messagesContainer.customReplies')}</div>
                    </div>
                  ) : null}
                  {isAiWriterAvailable && (
                    <ImproveWithAI
                      textInputRef={textInputRef}
                      lastResidentMessage={lastResidentMessage}
                      sendMessageAsSMS={sendMessageAsSMS}
                      providedText={providedText}
                      onInsertResult={onInsertAIResult}
                    />
                  )}
                </div>
              </div>
              <div className="message-editor__body">
                {(sendMessageAsSMS && smsMessagesLeft) || !sendMessageAsSMS ? (
                  <textarea
                    ref={textInputRef}
                    style={{ outline: 'none' }}
                    onKeyPress={(e) => onEnterPress(e)}
                    type="text"
                    value={providedText}
                    onChange={(e) => setProvidedText(e.target.value)}
                    maxLength="1200"
                    rows="2"
                    placeholder={t('chat:messagesContainer.typeMessage')}
                  />
                ) : (
                  <div className="no-sms">{t('chat:messagesContainer.outOfSmsCredits')}</div>
                )}
              </div>
              <div className="message-editor__footer">
                <div className={`add-button ${sendMessageAsSMS ? 'disabled' : ''}`}>
                  <ImagesUpload
                    isDisabled={sendMessageAsSMS}
                    pickImages={(files) => {
                      onSendWithAttachements(files)
                      setProvidedText('')
                    }}
                    limit={10}
                  />
                </div>
                <div
                  className={`send-button ${
                    !providedText || (sendMessageAsSMS && !smsMessagesLeft) ? 'disabled' : ''
                  }`}
                  onClick={onSendTextMessage}
                >
                  <Icon icon={'send'} viewBox={'0 0 25 20'} />
                </div>
              </div>
            </React.Fragment>
          </div>
        )}
      </div>
      <CustomReplyWindow
        isOpen={customRepliesWindowVisible}
        data={customReplies}
        onSelectAction={pickCustomReply}
        onClose={() => setCustomRepliesWindowVisible(false)}
      />
    </div>
  )
}

MessagesContainer.propTypes = {
  currentUserData: PropTypes.object,
  selectedChat: PropTypes.object,
  isLoading: PropTypes.bool,
  cancelLoading: PropTypes.func,
  managementUsers: PropTypes.array,
  participants: PropTypes.array,
  currentBuildingId: PropTypes.number,
  currentBuildingTimezone: PropTypes.string,
  closedConversationsData: PropTypes.array,
  textingOptOutInfo: PropTypes.bool,
  isAiWriterAvailable: PropTypes.bool,
}

export default MessagesContainer
